import type { ComponentType, HTMLAttributes } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';
import { invariant } from '@elfsight/lib-utils';
import type { Props as ReactSVGProps } from 'react-svg';
import { ReactSVG } from 'react-svg';
import { useDetectConflict } from './use-detect-conflict';

export type IconProps = HTMLAttributes<HTMLDivElement> & {
  component?: ComponentType;
  src?: string;
  fallbackSvgSrc?: string;
  size: number | [number, number];
  fill?: string;
  stroke?: string;
  loading?: ReactSVGProps['loading'];
  alt?: string;
  afterInjection?: ReactSVGProps['afterInjection'];
};

export const Icon = forwardRef<HTMLDivElement, IconProps>(function Icon(
  {
    style,
    component: IconComponent,
    src,
    fallbackSvgSrc,
    size,
    fill,
    stroke,
    loading,
    alt,
    afterInjection,
    ...forwardingProps
  },
  forwardingRef
) {
  const isConflict = useDetectConflict();
  const fileType = src && src.split('.').pop();
  const imageSrc = isConflict && fallbackSvgSrc ? fallbackSvgSrc : src;

  let width: number | undefined;
  let height: number | undefined;

  if (Array.isArray(size)) {
    invariant(
      size.length === 2,
      'If an array passed the size is expected to be exactly 2 values long ' +
        'which the first value defines width and the second defines height.'
    );

    [width, height] = size;
  } else {
    width = size;
    height = size;
  }

  if ((IconComponent && src) || (!IconComponent && !src)) {
    console.warn('Expected either component or src to be specified.');
    return null;
  }

  return (
    <IconContainer
      ref={forwardingRef}
      _width={width}
      _height={height}
      _fill={fill}
      style={{
        stroke,
        ...style
      }}
      {...forwardingProps}
    >
      {IconComponent && (
        <div>
          <IconComponent />
        </div>
      )}
      {src &&
        (isConflict || fileType !== 'svg' ? (
          <img src={imageSrc} alt={alt} />
        ) : (
          <ReactSVG
            src={src}
            loading={loading}
            afterInjection={afterInjection}
          />
        ))}
    </IconContainer>
  );
});

export const IconContainer = styled.div<{
  _width?: number;
  _height?: number;
  _fill?: string;
}>`
  ${({ _width, _height, _fill }) => `
    ${_width ? `width: ${_width}px;` : ``}
    ${_height ? `height: ${_height}px;` : ``}

    & > div {
      display: flex;
      align-items: center;
      justify-content: center;
      width: inherit;
      height: inherit;
    }

    &&& img, svg {
      transition: fill 0.2s;
      display: block;
      object-fit: contain;
      ${_width ? `width: ${_width}px;` : ``}
      ${_height ? `height: ${_height}px;` : ``}
      ${_fill ? `fill: ${_fill};` : ``}
    }
  `}
`;
